import { createStore } from 'vuex'
import api from '../services/api'

export default createStore({
  state: {
    user: null,
    token: localStorage.getItem('token') || null,
    refreshToken: localStorage.getItem('refreshToken') || null,
    stats: null,
    reports: [],
    users: [],
    reportsResponse: {}, // Adicionado para guardar resposta completa da API
    categories: [], // Adicionado para categorias
    snackbar: {
      show: false,
      text: '',
      color: 'success'
    }
  },
  
  getters: {
    isAuthenticated: state => !!state.token,
    isAdmin: state => state.user?.is_staff || state.user?.is_superuser || false
  },
  
  mutations: {
    SET_USER(state, user) {
      state.user = user
    },
    SET_TOKEN(state, token) {
      state.token = token
      if (token) {
        localStorage.setItem('token', token)
      } else {
        localStorage.removeItem('token')
      }
    },
    SET_REFRESH_TOKEN(state, refreshToken) {
      state.refreshToken = refreshToken
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken)
      } else {
        localStorage.removeItem('refreshToken')
      }
    },
    SET_STATS(state, stats) {
      state.stats = stats
    },
    SET_REPORTS(state, reports) {
      state.reports = reports
    },
    SET_USERS(state, users) {
      state.users = users
    },
    CLEAR_AUTH(state) {
      state.user = null
      state.token = null
      state.refreshToken = null
      localStorage.removeItem('token')
      localStorage.removeItem('refreshToken')
    },
    SET_REPORTS_RESPONSE(state, response) {
      state.reportsResponse = response
    },
    SET_CATEGORIES(state, categories) {
      state.categories = categories
    },
    SET_SNACKBAR(state, snackbar) {
      state.snackbar = snackbar
    }
  },
  
  actions: {
    async login({ commit }, credentials) {
      try {
        const response = await api.post('/auth/login/', credentials)
        const { access, refresh, user } = response.data
        
        commit('SET_TOKEN', access)
        commit('SET_REFRESH_TOKEN', refresh)
        commit('SET_USER', user)
        
        return { success: true }
      } catch (error) {
        // Detectar erro de troca obrigatória de senha
        if (error.response?.status === 403 && error.response?.data?.must_change_password) {
          return {
            success: false,
            mustChangePassword: true,
            error: error.response.data.error || 'É necessário trocar a senha antes de acessar.'
          }
        }
        return { 
          success: false, 
          error: error.response?.data?.error || 'Erro no login' 
        }
      }
    },

    async autoLogin({ commit }, token) {
      try {
        const response = await api.post('/auth/auto-login/', { token })
        const { access, refresh, user } = response.data

        commit('SET_TOKEN', access)
        commit('SET_REFRESH_TOKEN', refresh)
        commit('SET_USER', user)

        return { success: true }
      } catch (error) {
        return {
          success: false,
          error: error.response?.data?.error || 'Não foi possível validar o token.'
        }
      }
    },

    async forgotPassword(_, identifier) {
      try {
        const response = await api.post('/auth/forgot-password/', { identifier })
        return {
          success: true,
          message: response.data?.message || 'Instruções enviadas para o e-mail cadastrado.'
        }
      } catch (error) {
        return {
          success: false,
          error: error.response?.data?.error || 'Erro ao solicitar recuperação de senha.'
        }
      }
    },
    
    async logout({ commit }) {
      commit('CLEAR_AUTH')
    },
    
    async fetchProfile({ commit }) {
      try {
        const response = await api.get('/profile/')
        commit('SET_USER', response.data)
        return { success: true }
      } catch (error) {
        return { 
          success: false, 
          error: error.response?.data?.error || 'Erro ao carregar perfil' 
        }
      }
    },
    
    async fetchStats({ commit }) {
      try {
        const response = await api.get('/dashboard/stats/')
        commit('SET_STATS', response.data)
        return { success: true }
      } catch (error) {
        return { 
          success: false, 
          error: error.response?.data?.error || 'Erro ao carregar estatísticas' 
        }
      }
    },
    
    async fetchReports({ commit }, { page = 1, search = '', category = null } = {}) {
      try {
        let url = `/reports/?page=${page}`
        if (search) {
          url += `&search=${encodeURIComponent(search)}`
        }
        if (category) {
          url += `&category=${category}`
        }
        const response = await api.get(url)
        
        // Verificar se a resposta é paginada ou não
        const reports = response.data.results || response.data
        const responseData = response.data.results ? response.data : { results: response.data, count: response.data.length }
        
        commit('SET_REPORTS', reports)
        commit('SET_REPORTS_RESPONSE', responseData)
        return { success: true }
      } catch (error) {
        return { 
          success: false, 
          error: error.response?.data?.error || 'Erro ao carregar relatórios' 
        }
      }
    },
    
    async fetchUsers({ commit }) {
      try {
        const response = await api.get('/users/', { 
          params: { no_pagination: 'true' } 
        })
        commit('SET_USERS', response.data.results || response.data)
        return { success: true }
      } catch (error) {
        return { 
          success: false, 
          error: error.response?.data?.error || 'Erro ao carregar usuários' 
        }
      }
    },
    
    async updateProfile({ commit }, profileData) {
      try {
        const response = await api.patch('/profile/', profileData)
        commit('SET_USER', response.data)
        return { success: true }
      } catch (error) {
        return { 
          success: false, 
          error: error.response?.data?.error || 'Erro ao atualizar perfil' 
        }
      }
    },

    async updateThemePreference({ commit, state }, themePreference) {
      const normalizedTheme = themePreference === 'dark' ? 'dark' : 'light'
      try {
        const response = await api.patch('/profile/theme/', {
          theme_preference: normalizedTheme
        })
        commit('SET_USER', response.data)
        return { success: true }
      } catch (error) {
        commit('SET_USER', {
          ...state.user,
          theme_preference: normalizedTheme
        })
        return {
          success: false,
          error: error.response?.data?.error || 'Erro ao salvar preferência de tema'
        }
      }
    },
    
    async changePassword({ commit }, passwordData) {
      try {
        await api.post('/profile/change-password/', passwordData)
        return { success: true }
      } catch (error) {
        return { 
          success: false, 
          error: error.response?.data?.error || 'Erro ao alterar senha' 
        }
      }
    },
    
    async initializeAuth({ commit, state }) {
      if (state.token) {
        try {
          const response = await api.get('/profile/')
          commit('SET_USER', response.data)
          return { success: true }
        } catch (error) {
          // Se o token for inválido, limpar a autenticação
          commit('CLEAR_AUTH')
          return { success: false }
        }
      }
      return { success: true }
    },

    async fetchCategories({ commit }) {
      try {
        const response = await api.get('/categories/', { 
          params: { no_pagination: 'true' } 
        })
        commit('SET_CATEGORIES', response.data.results || response.data)
        return { success: true }
      } catch (error) {
        return { 
          success: false, 
          error: error.response?.data?.error || 'Erro ao carregar categorias' 
        }
      }
    }
  }
}) 